import React, { useState } from 'react';
import { useLocation } from 'react-router';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import env from 'environments';
import flow from '@prosperstack/flow';
import { useQueryClient } from 'react-query';
import { useSignature } from '../hooks/use-signature';
import ReactivationInstructionsModal from './ReactivationInstructionsModal';

const CANCELED = 'canceled';

const CancelMembershipButton = ({ subscription }) => {
  const clientId = env.VITE_CLIENT_ID;
  const { generateSignature, isLoading } = useSignature();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);

  // Check if we're on Plans & Billing page (where banner is visible)
  const isOnPlansAndBilling = location.pathname === '/plans-and-billing';

  // Handle nested subscription data from Chargebee API
  // If data comes as { list: [{ subscription: {...} }] }, unwrap it
  const actualSubscription = subscription?.list?.[0]?.subscription || subscription;

  const handleCancelMembership = async () => {
    // Use subscriptionId or _id from Chargebee
    const subId = actualSubscription.subscriptionId || actualSubscription.id || actualSubscription._id;
    const payload = {
      clientId,
      subscription: {
        platformId: subId.toString(),
      },
    };

    try {
      const signature = await generateSignature(payload);
      flow(
        {
          payload: JSON.stringify(payload),
          signature,
          display: 'modal', // Use modal instead of full-screen
        },
        {
          onCompleted: async () => {
            // Reload the page to reflect the new subscription state
            window.location.reload();
          },
          onCanceled: () => {
            // User closed the modal without completing
            setShowConfirmation(false);
          },
        },
      );
    } catch (error) {
      console.error('Error while canceling membership:', error);
    }
  };

  // Use chargebeeSubscriptionStatus if available, otherwise fall back to status
  const chargebeeStatus = actualSubscription?.chargebeeSubscriptionStatus;
  const dbStatus = actualSubscription?.status || actualSubscription?.state;
  const subscriptionStatus = chargebeeStatus || dbStatus;

  // Chargebee API returns _id (MongoDB ID), not id
  const subscriptionId = actualSubscription?.id || actualSubscription?._id;
  const isSubscriptionLoading =
    !subscriptionId || !subscriptionStatus || subscriptionStatus === '';

  const isSubscriptionCanceled = subscriptionStatus === CANCELED || subscriptionStatus === 'cancelled';

  // Check for scheduled cancellation using backend fields
  const scheduledCancelDate = actualSubscription?.cancellationScheduledAt ||
                               actualSubscription?.cancelled_at ||
                               actualSubscription?.cancelledAt ||
                               actualSubscription?.cancel_date ||
                               actualSubscription?.cancelAt;

  const currentTermEnd = actualSubscription?.currentTermEnd;

  // Backend returns 'cancellation_scheduled' in status and 'non_renewing' in chargebeeSubscriptionStatus
  const isScheduledForCancellation =
    !isSubscriptionCanceled &&
    (dbStatus === 'cancellation_scheduled' ||
     chargebeeStatus === 'non_renewing' ||
     !!scheduledCancelDate ||
     !!actualSubscription?.cancelScheduleCreatedAt);

  // Debug logging to see subscription data
  console.log('🔍 Subscription check:', {
    chargebeeStatus,
    dbStatus,
    subscriptionStatus,
    isScheduledForCancellation,
    scheduledCancelDate,
    currentTermEnd,
    cancelScheduleCreatedAt: actualSubscription?.cancelScheduleCreatedAt,
    hasNestedStructure: !!subscription?.list,
    allFields: Object.keys(actualSubscription || {}),
    fullSubscriptionData: JSON.stringify(actualSubscription, null, 2)
  });

  return (
    <>
      {isSubscriptionLoading ? (
        <div role="status" className="animate-pulse inline-block">
          <div className="w-32 h-9 bg-gray-200 rounded" />
        </div>
      ) : (
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={() => {
              // Show instructions modal for scheduled cancellations, confirmation for others
              if (isScheduledForCancellation) {
                setShowInstructionsModal(true);
              } else {
                setShowConfirmation(true);
              }
            }}
            className={`px-4 py-2 text-sm rounded cursor-pointer transition-colors ${
              isSubscriptionCanceled
                ? 'bg-green-600 text-white hover:bg-green-700 font-semibold shadow-md'
                : isScheduledForCancellation
                  ? 'bg-primary-500 text-neutral-800 hover:bg-primary-600 font-semibold shadow-md'
                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
            }`}
            disabled={isLoading}
          >
            {isSubscriptionCanceled
              ? 'Reactivate subscription'
              : isScheduledForCancellation
                ? 'How to reactivate my subscription?'
                : 'Cancel subscription'}
          </button>

          {isScheduledForCancellation && scheduledCancelDate && !isOnPlansAndBilling && (
            <p className="text-xs text-gray-600">
              Your subscription will end on{' '}
              <span className="font-semibold">
                {new Date(scheduledCancelDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </p>
          )}

          {!isScheduledForCancellation && !isSubscriptionCanceled && currentTermEnd && (
            <p className="text-xs text-gray-500">
              Current billing period ends{' '}
              {new Date(currentTermEnd).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          )}
          {/* Reactivation Instructions Modal */}
          <ReactivationInstructionsModal
            isOpen={showInstructionsModal}
            onClose={() => setShowInstructionsModal(false)}
          />

          {/* Confirmation Dialog */}
          {showConfirmation && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={() => setShowConfirmation(false)}
            >
              <div
                className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-tight">
                    {isSubscriptionCanceled
                      ? 'Do you want to reactivate your subscription?'
                      : isScheduledForCancellation
                        ? 'Cancel your scheduled cancellation?'
                        : 'Are you sure you want to cancel your membership?'}
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    {isSubscriptionCanceled
                      ? 'You can reactivate your subscription and regain access to all features. This will guide you through the reactivation process.'
                      : isScheduledForCancellation
                        ? 'Your subscription is currently scheduled to be canceled. You can undo this cancellation and keep your subscription active. This will guide you through the process to keep your subscription.'
                        : 'Canceling will DELETE your lead capture pages, your custom books, your tailored marketing assets, your remaining credits, and your leads database.'}
                  </p>
                  {!isSubscriptionCanceled && !isScheduledForCancellation && (
                    <p className="text-sm text-gray-600">
                      To discuss additional membership options, contact us by email at{' '}
                      <a href="mailto:support@authorify.com" className="text-blue-600 hover:underline">
                        support@authorify.com
                      </a>.
                    </p>
                  )}
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleCancelMembership}
                    disabled={isLoading}
                    className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded cursor-pointer transition-colors"
                  >
                    {isSubscriptionCanceled
                      ? 'Continue to Reactivation'
                      : isScheduledForCancellation
                        ? 'Keep My Subscription'
                        : 'Cancel Anyway'}
                  </button>
                  <Button
                    type="primary"
                    onClick={() => setShowConfirmation(false)}
                    disabled={isLoading}
                  >
                    {isSubscriptionCanceled
                      ? 'Go Back'
                      : isScheduledForCancellation
                        ? 'Go Back'
                        : 'Keep My Membership'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CancelMembershipButton;
